import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./styles.css";

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error("[Ripple] Uncaught render error:", error, info);
  }

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          padding: "48px 24px",
          maxWidth: "640px",
          margin: "80px auto",
          fontFamily: "'Inter', sans-serif",
          color: "#0f0c29",
          background: "rgba(255, 255, 255, 0.92)",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          borderRadius: "16px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.09)",
        }}
      >
        <h1 style={{ margin: "0 0 12px", fontFamily: "'Playfair Display', serif" }}>
          Something went wrong
        </h1>
        <p style={{ margin: "0 0 16px", color: "#3d3960", lineHeight: 1.6 }}>
          Ripple hit an unexpected error while rendering. Try reloading the page. If
          the problem persists, the console has more detail.
        </p>
        <pre
          style={{
            margin: 0,
            padding: "12px 14px",
            background: "rgba(0, 0, 0, 0.04)",
            borderRadius: "8px",
            fontSize: "0.82rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "#3d3960",
          }}
        >
          {this.state.error.message}
        </pre>
      </div>
    );
  }
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root element not found. Check public/index.html for div#root.");
}

createRoot(container).render(
  <ErrorBoundary>
    <App />
  </ErrorBoundary>
);
